import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Renderer2,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation
} from '@angular/core';
import { Store, Select, Actions, ofActionDispatched } from '@ngxs/store';
import { ActivatedRoute } from '@angular/router';
import { Subject, Observable, combineLatest, fromEvent } from 'rxjs';
import { ViewService } from '../../view.service';
import { EnginePositionModel } from 'src/app/engine/models/engine-position.model';
import { EngineVisibilityEnum } from 'src/app/engine/models/engine-visibility.enum';
import { SetEnginePosition, ClickNode, PlayAnimation, SetDisplay, SetVisible } from 'src/app/engine/state/engine.actions';
import { ViewState } from '../../state/view.state';
import { takeUntil, map, filter, distinctUntilChanged, debounceTime, pairwise, take } from 'rxjs/operators';
import { ViewActions } from '../../state/view.actions';
import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout';
import { ViewBreakpoint } from '../../models/view-breakpoint.enum';
import { ViewLayoutStatus } from '../../models/view-layout-status.enum';
import { ViewLayoutModel } from '../../models/view-layout.model';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { AnimationService } from 'src/app/engine/animation.service';
import { ViewMode } from '../../models/view-mode.enum';
import * as Mousetrap from 'mousetrap';
import * as resizeDetectorMaker from 'element-resize-detector';
import { Fullscreen } from 'src/app/shared/libraries/fullscreen/fullscreen';
import { EngineProgressState } from 'src/app/engine/state/progress/engine.progress.state';
import { SetLabelsCollapsed, SetLabelsDraggable, EnableContextLabel } from 'src/app/story/state/labels/story.labels.actions';
import {
  QueryNodeInfo,
  SelectNode,
  ToggleVisibleNodes,
  HideInverse,
  Isolate,
  PaintNodes,
  ResetEngineNodes
} from 'src/app/engine/state/nodes/engine.nodes.actions';
import { EngineState } from 'src/app/engine/state/engine.state';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { trigger, transition, style, animate } from '@angular/animations';
import { ViewToolsMode } from '../../models/view-tools-mode.enum';
import { LoaderTypeEnum } from '../../models/loader-type.enum';
import { isNil } from 'lodash';
import { LayoutService } from '../../layout.service';
import { ViewLayout } from '../../models/view-layout.enum';
import { ViewDrawerComponent } from '../../components/view-drawer/view-drawer.component';
import { LabelCollapseModeEnum } from 'src/app/story/models/labels/label-collapse-mode';
import { LoadStorySuccess } from 'src/app/story/state/story.actions';
import { ResetChaptersModfied, SetChapterModfied } from 'src/app/story/state/chapters/story.chapters.actions';
import { RevertChapterSynchronization } from 'src/app/history/state/history.actions';
import { HistoryService } from 'src/app/history/history.service';
import { SetTheme } from 'src/app/layout/state/layout.actions';
import { LayoutThemeEnum } from 'src/app/layout/models/layout-theme.enum';
import { SetProgress } from 'src/app/engine/state/progress/engine.progress.actions';
import { StoryService } from 'src/app/story/story.service';
import { CleanupService } from 'src/app/shared/cleanup.service';
import { StoryState } from 'src/app/story/state/story.state';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';

const drawerHeight = 64;

@Component({
  selector: 'app-view-page',
  templateUrl: './view-page.component.html',
  styleUrls: ['./view-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(200px)'
        }),
        animate('500ms ease-out', style({
          opacity: 1,
          transform: '*'
        }))
      ]),
      transition(':leave', [
        animate('300ms', style({
          transform: 'translateY(200px)',
          opacity: 0
        }))
      ])
    ])
  ]
})
export class ViewPageComponent implements OnInit, AfterViewInit, OnDestroy {
  // ---------- Child Elements ----------
  @ViewChild('engineContainer', { static: true })
  engineContainer: ElementRef;

  @ViewChild('viewContainer', { static: true })
  viewContainer: ElementRef;

  @ViewChild('view', { static: true })
  view: ElementRef;

  @ViewChild('viewMenu', { static: false })
  viewMenu: ElementRef;

  @ViewChild('viewNavBar', { static: false })
  viewNavBar: ElementRef;

  @ViewChild('viewLogo', { static: false })
  viewLogo: ElementRef;

  @ViewChild('viewSceneBase', { static: true })
  viewSceneBase: ElementRef;

  @ViewChild('drawer', { static: false, read: ViewDrawerComponent })
  drawer: ViewDrawerComponent;



  // ---------- Observables ----------
  @Select(ViewState.getLayout)
  layout$: Observable<ViewLayoutModel>;

  @Select(ViewState.getBreakpoint)
  breakpoint$: Observable<ViewBreakpoint>;

  @Select(ViewState.getInputEnabled)
  inputEnabled$: Observable<boolean>;

  @Select(ViewState.getFullscreenActive)
  fullscreenActive$: Observable<boolean>;

  @Select(ViewState.getDrawerCoversContent)
  drawerCoversContent$: Observable<boolean>;

  @Select(ViewState.getShowHelp)
  showHelp$: Observable<boolean>;

  @Select(ViewState.getLoaderActive)
  loaderActive$: Observable<boolean>;

  @Select(ViewState.getLoaderType)
  loaderType$: Observable<LoaderTypeEnum>;

  @Select(ViewState.getUsePrintImage)
  usePrintImage$: Observable<boolean>;

  @Select(EngineProgressState.getStoryLoadProgress)
  loadProgress$: Observable<number>;



  // ---------- Class Variables ----------
  viewBreakpoint = ViewBreakpoint;
  viewLayoutStatus = ViewLayoutStatus;
  viewMode = ViewMode;
  viewLayout = ViewLayout;
  columnLayoutStatus = ViewLayoutStatus.Hide;
  drawerExpandable = false;
  drawerOpen = false;
  drawerY = 0;
  loadProgress = 0;
  viewToolsMode = ViewToolsMode;
  private ngUnsubscribe = new Subject();



  // ---------- Lifecycle hooks ----------
  constructor(
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    private store: Store,
    private viewService: ViewService,
    private historyService: HistoryService,
    private breakpointObserver: BreakpointObserver,
    private changeDetectorRef: ChangeDetectorRef,
    private actions: Actions,
    private animationService: AnimationService,
    private layoutService: LayoutService,

    private engineService: EngineService,
    private cleanupService: CleanupService
  ) { }

  ngOnInit() {
    // Reset scene visibility so we don't see a FOUC of the last scene
    // This must happen before any meaningful browser render
    this.resetScene();

    // Load content.
    const queryParams = this.viewService.normalizeQueryParams(this.activatedRoute.snapshot.queryParams);
    this.viewService.initStory(queryParams.id, queryParams.type, queryParams.lang);
  }

  ngAfterViewInit() {
    this.store.dispatch(new SetLabelsDraggable({
      tag: true,
      pin: false
    }));

    this.checkCapabilities();
    this.initStoryDirty();
    this.initSizeObservers();
    this.initLayoutObserver();
    this.initBreakpointObservers();
    this.initKeyboardObserver();
    this.initLoadProgressObserver();
    this.initLoaderObserver();
    this.initResponsiveLabels();
    
    this.initNodeObserver();
    this.initChapterObserver();
    this.initAnimationObserver();

    this.positionRelativeElements(this.drawerY);
  }

  ngOnDestroy() {
    this.store.dispatch(new SetDisplay(false));
    this.store.dispatch(new ResetEngineNodes());
    this.store.dispatch(new SetProgress(0, 0));

    if (Fullscreen.isFullscreen()) {
      Fullscreen.exitFullscreen();
    }

    this.unbindKeyboard();

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }



  // ---------- Capability detection ----------
  checkCapabilities() {
    const mouseOver = this.renderer.listen('window', 'mouseover', () => {
      const canHover = true;
      mouseOver();

      this.store.dispatch(new ViewActions.SetCanHover(canHover));
    });

    const canTouch = (('ontouchstart' in window)
      || (navigator.maxTouchPoints > 0)
      || (navigator.msMaxTouchPoints > 0));

    this.store.dispatch(new ViewActions.SetCanTouch(canTouch));
    this.store.dispatch(new ViewActions.SetCanFullscreen(Fullscreen.canFullscreen()));
  }



  // ---------- Listeners ----------
  initBreakpointObservers() {
    this.breakpoint$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.handleResize();
      });

    // < 600
    const small$ = this.breakpointObserver.observe([
      Breakpoints.XSmall
    ]);

    // < 601 - 959
    const medium$ = this.breakpointObserver.observe([
      Breakpoints.Small
    ]);

    // > 960
    const large$ = this.breakpointObserver.observe([
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge
    ]);

    // Observe all breakpoints
    combineLatest(small$, medium$, large$)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        map(([small, medium, large]) => {
          return small.matches ? ViewBreakpoint.Small : medium.matches ? ViewBreakpoint.Medium : large.matches ? ViewBreakpoint.Large : null;
        }),
        distinctUntilChanged(),
        filter(point => point != null) // Returns 'undefined' when rapidly resizing
      ).subscribe((breakpoint: ViewBreakpoint) => {
        this.store.dispatch(new ViewActions.SetBreakpoint(breakpoint));
      });
  }

  initResponsiveLabels() {
    // observer: window size change (throttle)
    const windowReseize$ = fromEvent(window, 'resize')
      .pipe(debounceTime(100));

    // observe: breakpoint changes
    const labelBreakpoint$ = this.store.select(ViewState.getLabelsBreakpoint);

    // observe: label selection/de-selection
    const labelSelected$ = this.store.select(StoryLabelsState.getSelectedLabel);

    // observe: label collapse state
    const labelsCollapsed$ = this.store.select(StoryLabelsState.getLabelsCollapsed);

    // set: label info on selection
    labelSelected$
      .pipe(
        // ignore if not collapsed
        filter(() => this.store.selectSnapshot(StoryLabelsState.getLabelsCollapsed)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((label) => {
        if (label) {
          this.viewService.toggleLabelInfo(true);
        } else {
          this.viewService.toggleLabelInfo(false);
        }
      });

    // set: label info on collapse
    labelsCollapsed$
      .pipe(
        // validate selected label
        filter(() => !!this.store.selectSnapshot(StoryLabelsState.getSelectedLabel)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((collapsed) => {
        this.viewService.toggleLabelInfo(collapsed);
      });

    // combine various label breakpoint triggers
    combineLatest(
      windowReseize$,
      labelBreakpoint$
    )
      .pipe(
        // ignore unless breakpoint enabled
        filter(([, labelBreakpoint]) => !isNil(labelBreakpoint)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.checkLabelsCollapsed();
      });

    // set initial
    this.checkLabelsCollapsed();
  }

  initSizeObservers() {
    const resizeDetector = resizeDetectorMaker({ strategy: 'scroll' });

    // Listen to view page resize
    const windowResize$ = fromEvent(window, 'resize');
    windowResize$
      .pipe(
        debounceTime(10),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.updateViewHeight();
      });

    // Listen to engine container resize
    const engineResize$ = new Observable(subscriber => {
      resizeDetector.listenTo(this.engineContainer.nativeElement, () => {
        subscriber.next();
      });
    });

    engineResize$
      .pipe(
        debounceTime(10)
      )
      .subscribe(() => {
        this.resizeEngineContainer();
      });
  }

  initLayoutObserver() {
    this.layout$
      .pipe(
        filter(layout => layout != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((layout) => {
        this.refreshLayout();
      });
  }

  initKeyboardObserver() {
    this.inputEnabled$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(enabled => {
      (enabled) ? this.bindKeyboard() : this.unbindKeyboard();
    });
  }

  initNodeObserver() {
    this.actions
      .pipe(
        ofActionDispatched(ClickNode),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action: ClickNode) => {
        this.viewService.delegateNodeClick(action.node, action.event);
      });

    // handle node info querying
    this.actions.pipe(
      ofActionDispatched(SelectNode),
      // validate selection mode
      filter(() => this.store.selectSnapshot(EngineState.getActiveTool) === ToolEnum.Select),
      map(action => action.node),
      pairwise(),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(([oldNode, newNode]) => {
      const layout = this.store.selectSnapshot(ViewState.getLayout);
      const showNodeInfo = layout.nodeInfo.status === ViewLayoutStatus.Show;

      if (showNodeInfo) {
        // Hide info column when no node selected
        if (newNode == null || newNode.id !== oldNode) {
          this.viewService.toggleNodeInfo(false);

          // Look up node info
        } else if (newNode) {
          this.store.dispatch(new QueryNodeInfo(newNode));
        }
      }

    });
  }

  initChapterObserver() {
    // Chapter change
    this.store.select(StoryChaptersState.getSelectedChapterId)
      .pipe(
        filter(chapter => chapter != null),
        map(() => this.store.selectSnapshot(StoryChaptersState.getSelectedChapter)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((chapter) => {
        this.viewService.initHistory();
        this.viewService.initChapter(chapter);
        this.refreshLayout();
      });
  }

  initAnimationObserver() {
    // Chapter change
    this.store.select(StoryChaptersState.getSelectedChapter).pipe(
      filter((chapter) => chapter != null),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((chapter) => {
      this.handleAnimationReady(chapter);
    });
  }

  initLoadProgressObserver() {
    this.loadProgress$
      .pipe(
        takeUntil(
          this.ngUnsubscribe
        )
      )
      .subscribe((progress) => {
        this.loadProgress = progress * 100;
        this.changeDetectorRef.markForCheck();
      });
  }

  initLoaderObserver() {
    this.loaderActive$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((loader) => {
      this.viewService.resetLayout();

      if (loader) {
        this.historyService.resetChapter();
        this.viewService.resetCamera();
        this.viewService.toggleLabels(false);
      } else {
        this.viewService.toggleLabels(true);
      }
    });
  }

  // Sets the modified state of the story chapters
  initStoryDirty() {
    // reset dirty state on new story
    this.actions
      .pipe(
        ofActionDispatched(LoadStorySuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(new ResetChaptersModfied());
      });

    // mark dirty: explicit user actions
    this.actions
      .pipe(
        ofActionDispatched(
          // visibility
          ToggleVisibleNodes,
          HideInverse,
          Isolate,
          // paint
          PaintNodes
        ),
        debounceTime(300),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        const selectedChapterId = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
        this.store.dispatch(new SetChapterModfied(selectedChapterId, true));
      });

    // mark clean: reset chapter action
    this.actions
      .pipe(
        ofActionDispatched(RevertChapterSynchronization),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        const selectedChapterId = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
        this.store.dispatch(new SetChapterModfied(selectedChapterId, false));
      });
  }

  // ---------- Event Handlers ----------
  handleAnimationReady(chapter) {
    const animationReady = chapter.readyState.animation;
    const isAnimated = this.animationService.isChapterAnimated(chapter);

    if (isAnimated && animationReady) {
      this.store.dispatch(new PlayAnimation(true));
    }
  }

  handleColumnToggle() {
    this.viewService.toggleColumn();
    this.changeDetectorRef.markForCheck();
  }

  handleDrawerOffsetChange(drawerOffsetY: number) {
    this.drawerY = drawerOffsetY;
    this.drawerOpen = drawerOffsetY !== -drawerHeight;

    this.positionRelativeElements(this.drawerY);
  }

  handleExitClick() {
    this.viewService.toggleLoader(true);
  }

  checkLabelsCollapsed() {
    const viewBreakpoint = this.store.selectSnapshot(ViewState.getBreakpoint);
    const labelBreakpoint = this.store.selectSnapshot(ViewState.getLabelsBreakpoint);
    const collapsed = this.isLabelBreaking(labelBreakpoint);
    
    // get collapsed mode: based on view breakpoint 
    const collapsedMode = viewBreakpoint === ViewBreakpoint.Large
      ? LabelCollapseModeEnum.Toggle
      : LabelCollapseModeEnum.None;

    this.store.dispatch(new SetLabelsCollapsed(collapsed, collapsedMode));
  }

  isLabelBreaking(labelBreakpoint: number): boolean {
    return window.innerWidth < labelBreakpoint;
  }

  bindKeyboard() {
    // Tool switching
    Mousetrap.bind('1', () => this.viewService.selectTool(ToolEnum.Select));
    Mousetrap.bind('2', () => this.viewService.selectTool(ToolEnum.Hide));
    Mousetrap.bind('3', () => this.viewService.selectTool(ToolEnum.Fade));

    // Column toggle
    Mousetrap.bind('meta+,', (event) => {
      event.preventDefault();
      this.viewService.toggleColumn();
      this.changeDetectorRef.markForCheck();
    });

    // Presentation toggle
    Mousetrap.bind('meta+enter', (event) => {
      event.preventDefault();
      this.viewService.togglePresentation();
    });

    // Chapter browser
    Mousetrap.bind('.', () => this.viewService.nextChapter());
    Mousetrap.bind(',', () => this.viewService.previousChapter());

    // Delete
    Mousetrap.bind(['del', 'backspace'], (event) => {
      event.preventDefault();
      this.viewService.hideSelectedStructures();
    });

    // Help
    Mousetrap.bind('?', () => this.viewService.toggleHelp());

    // Play / Pause
    Mousetrap.bind('space', (event) => {
      event.preventDefault();
      this.viewService.toggleAnimation();
    });
  }

  unbindKeyboard() {
    Mousetrap.reset();
  }



  // ---------- Layout ----------
  resizeEngineContainer() {
    const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = this.engineContainer.nativeElement;
    const position = new EnginePositionModel(offsetLeft, offsetTop, offsetWidth, offsetHeight, EngineVisibilityEnum.Shown);

    this.store.dispatch(new SetEnginePosition(position));
  }

  // Mobile devices have variable viewheight because the browser bar can scroll in and out of view
  // This workaround determines the available window space and uses that as a viewheight unit
  updateViewHeight() {
    const vh = window.innerHeight * 0.01;
    this.view.nativeElement.style.setProperty('--vh', `${vh}px`);
  }

  handleResize() {
    this.viewService.resetLayout();
  }

  // Previous scene is kept in memory so we don't have to reinitialize the entire engine every pageload.
  // We also want to keep the scene fully in memory so that transitions from /view to /edit don't require a reload.
  // This function hides the engine, waits until first meaningful render, then shows it, guaranteeing that the content
  // the user sees is accurate. 
  resetScene() {
    this.store.dispatch(new SetDisplay(true));
    this.store.dispatch(new SetVisible(false));

    this.engineService
      .getEvent(EventsEnum.AssetsLoadProgress)
      .pipe(
        take(1),
        takeUntil(this.ngUnsubscribe)
      ).subscribe(() => {
        this.store.dispatch(new SetVisible(true));
      });
  }

  
  refreshLayout() {
    const layout = this.store.selectSnapshot(ViewState.getLayout);

    // Update visible elements
    this.columnLayoutStatus = layout.column.status;

    this.updateViewHeight();
    this.resizeEngineContainer();

    // Revert tool selection when tools isn't visible
    if (layout.tools.status !== ViewLayoutStatus.Show) {
      this.viewService.selectTool(ToolEnum.Select);
    }

    // Update label visibility status
    this.store.dispatch(new EnableContextLabel(layout.labelInfo.enable, layout.nodeInfo.enable));
    

    // Add a timeout to allow drawer time to render
    setTimeout(() => {
      if (this.drawer) {
        const drawerMode = this.viewService.inferDrawerMode(layout);
        this.drawer.toggleDrawer(drawerMode === ViewMode.NodeInfo);

        // Update when drawer is expandable.
        // - The tools menu is not expandable
        this.drawerExpandable = drawerMode !== ViewMode.Tools;
      }

      // Update elements relative to the drawer
      this.positionRelativeElements(this.drawerY);
    });
  }
  
  positionRelativeElements(drawerOffsetY = 0) {
    const drawerCoversContent = this.store.selectSnapshot(ViewState.getDrawerCoversContent);
    const baseDrawerOffset = (drawerCoversContent) ? -drawerHeight : 0;

    const isMenuVisible = this.isVisible(ViewLayout.Menu);
    const isDrawerVisible = this.isVisible(ViewLayout.Drawer);

    // Offset menu relative to drawer being open or closed
    if (isMenuVisible && this.viewMenu != null) {
      const menuOffsetY = (isDrawerVisible) ? drawerOffsetY + 16 : -8;
      this.renderer.setStyle(this.viewMenu.nativeElement, 'transform', `translate3d(0, ${menuOffsetY}px, 0)`);
    }

    // Offset nav bar to fixed position
    const navBarOffsetY = 
      (isMenuVisible && isDrawerVisible) ? baseDrawerOffset + -28 : 
      isMenuVisible ? -52 : 
      -8;

    if (this.viewNavBar != null) {
      this.renderer.setStyle(this.viewNavBar.nativeElement, 'transform', `translate3d(0, ${navBarOffsetY}px, 0)`);
    }

    const baseOffsetY = (isDrawerVisible) ? baseDrawerOffset + -8 : -8;

    // Offset base container bar
    this.renderer.setStyle(this.viewSceneBase.nativeElement, 'transform', `translate3d(0, ${baseOffsetY}px, 0)`);
  }

  isVisible(layout: ViewLayout) {
    return this.layoutService.isVisible(layout);
  }
}
